import router from '@/router/index.js';
import mixin from "@/mixin/index.js";
import functions from "@/router/functions.js";

//CONSTANTS
import labels from '@/constants/labels';
import messages from '@/constants/messages.js';
import parameters from '@/constants/parameters.js';

//PLUGINS
import { analytics } from "@/plugins/firebase.js";
import { preventCopy } from "@/plugins/copy.plugins.js";
import storage from '@/plugins/storage.plugins.js';

//SERVICES
import loginServices from '@/services/login.services.js';
import versionsServices from '@/services/versions.services.js';
import entitiesServices from '@/services/entities.services.js';
import partnersServices from '@/services/partners.services.js';

const root = { root: true };

//ACTIONS
export default {

    async updateApp({ state, dispatch, commit }) {
        let versionStatus = parameters.OK_APP_VERSION;
        if (!!router.app._route.query.email && !!router.app._route.query.code) {
            commit("setEmailConfirmation", false);
            await dispatch('general/confirmEmailCode', {
                email: router.app._route.query.email.trim(),
                code: router.app._route.query.code
            }, root);
        }
        try {
            if (state.user) {
                dispatch('preferences/getPreferencesByMode', { mode: 'searches' }, root);
            }
            let response = await versionsServices.getVersions(state.user);
            if (!!router.app._route.query.purchases) {
                dispatch("showMessage", messages.PURCHASE_SUCCESS);
            } else if (!!state.user && !mixin.methods._isCordovaApp() && !state.user.premiumStatus && process.env.VUE_APP_ENV !== "partners") {
                dispatch('purchases/openPurchases', null, root);
            }
            versionStatus = response.status;
            state.trends = response.payload.trends;
        } catch (err) {
            console.log(err);
        } finally {
            switch (versionStatus) {
                case parameters.OK_APP_VERSION:
                    if (router.app._route.name == "update") dispatch('general/routeRedirect', { name: 'home' }, root);
                    else commit("general/setLoadingOff", null, root);
                    break;
                case parameters.OUTDATED_APP_VERSION:
                    alert(messages.OUTDATED_APP_VERSION);
                    dispatch('general/routeRedirect', { name: 'home' }, root);
                    break;
                case parameters.FORBIDDEN_APP_VERSION:
                    alert(messages.FORBIDDEN_APP_VERSION);
                    break;
                default:
                    dispatch('general/routeRedirect', { name: 'home' }, root);
            }
        }
    },

    async getUserProvidersFromEmail(_store, email) {
        try {
            return await loginServices.getUserProvidersFromEmail(email);
        } catch (err) {
            dispatch("showError", messages.ERROR);
        }
    },

    async loginWithEmail({ state, dispatch }, { email, name, retry, password }) {
        let response = false;
        try {
            password = !!password ? mixin.methods._hash(password) : null;
            dispatch("general/logEvent", { name: 'loginWithEmail', object: { 'id': email } }, root);
            await loginServices.loginWithEmail(email, name, retry, password);
            response = true;
            const detectOutsideLogin = (evt) => {
                if (evt.isTrusted && !!evt.newValue && !evt.oldValue && evt.key === 'vetalfa') {
                    window.location.reload();
                }
            };
            window.removeEventListener('storage', detectOutsideLogin)
            window.addEventListener('storage', detectOutsideLogin);
        } catch {
            dispatch("showError", state.user ? messages.EMAIL_CONFIRMATION_ERROR : messages.ERROR);
        }
        return response;
    },

    async confirmEmailCode({ commit, state, dispatch }, { code, email }) {
        dispatch("general/logEvent", { name: 'confirmEmailCode', object: { 'id': email } }, root);
        try {
            state.user = await loginServices.confirmEmailCode(code, email);
            dispatch("general/logEvent", { name: 'confirmEmailCodeSuccess', object: { 'id': email } }, root);
            storage.storageUpsert("user", null, state.user, _ => { }, _ => { });
            analytics.setUserId(state.user.uid);
            dispatch("showMessage", messages.SUCCESS);
            commit("setFromLogin", true);
            return true;
        } catch (e) {
            dispatch("general/logEvent", { name: 'confirmEmailCodeError', object: { 'id': email } }, root);
            if (e.message && e.message.includes('Network Error') && mixin.methods._isCordovaApp()) {
                dispatch("showAlert", { text: messages.ERROR_NETWORK, color: 'black' });
            } else {
                dispatch("showError", messages.INVALID_EMAIL_CODE);
            }
            return false;
        }
    },

    async loginWithPassword({ commit, state, dispatch }, { password, email }) {
        dispatch("general/logEvent", { name: 'loginWithPassword', object: { 'id': email } }, root);
        try {
            password = !!password ? mixin.methods._hash(password) : null;
            state.user = await loginServices.loginWithPassword(password, email);
            dispatch("general/logEvent", { name: 'loginWithPasswordSuccess', object: { 'id': email } }, root);
            storage.storageUpsert("user", null, state.user, _ => { }, _ => { });
            analytics.setUserId(state.user.uid);
            dispatch("showMessage", messages.SUCCESS_EMAIL_PASSWORD_CONFIRMATION);
            commit("setFromLogin", true);
            return true;
        } catch (e) {
            dispatch("general/logEvent", { name: 'confirmEmailCodeError', object: { 'id': email } }, root);
            if (e.message && e.message.includes('Network Error') && mixin.methods._isCordovaApp()) {
                dispatch("showAlert", { text: messages.ERROR_NETWORK, color: 'black' });
            } else {
                dispatch("showError", messages.INVALID_EMAIL_PASSWORD);
            }
            return false;
        }
    },

    async postActivity({ state, dispatch }) {
        const dayMiliseconds = 40000000;
        const now = new Date().getTime();
        const hasActivity = state.postActivity == null || (now - state.postActivity) > dayMiliseconds;
        if (hasActivity) {
            dispatch("general/logEvent", mixin.methods._getPlatform(), root);
            dispatch("general/logEvent", { name: 'postActivity', object: { 'id': state.user.uid } }, root);
            state.postActivity = now;
            loginServices.postActivity(state.user.uid);
        }
    },

    showDisclaimer({ commit, state }) {
        if (!state.disclaimer) {
            commit('setDisclaimer', true);
            commit('setConfirmModel', {
                active: true,
                alert: true,
                text: labels.DISCLAIMER,
                confirmText: labels.CONTINUE,
            });
        }
    },

    async initializeApp({ commit, state, dispatch }) {
        if (!!state.user && (!state.user.email || !state.user.uid || state.user.email.includes('privaterelay.appleid'))) {
            return dispatch('completeLogout');
        }
        dispatch('preferences/getPreferencesByMode', { mode: 'searches' }, root);
        dispatch("postActivity");
    },

    openQuestions({ dispatch, state }) {
        dispatch("general/logEvent", { name: 'openQuestions', object: { 'id': state.user.uid } }, root);
        dispatch("routeRedirect", { name: 'questions' });
    },

    openEmailDialog({ commit }) {
        commit("setEmailConfirmation", true);
    },

    logEvent({ }, evt) {
        if (process.env.NODE_ENV == 'production') {
            if (typeof evt == 'string') {
                analytics.logEvent(evt);
            } else {
                analytics.logEvent(evt.name, evt.object);
            }
        }
    },

    openInternalUrl(_store, { url }) {
        functions.getLinkRedirectFromDynamicLink({ deepLink: url });
    },

    openExternalUrl({ }, { url, inAppBrowser }) {
        if (url.includes('http://')) {
            url = url.replace('http', 'https');
        }
        if (!url.includes('https')) {
            url = 'https://' + url;
        }
        analytics.logEvent('open_external_url', { 'url': url });

        if (mixin.methods._isCordovaApp() && url != parameters.INSTAGRAM_LINK && url != parameters.APP_ON_PLAY_STORE && inAppBrowser) {
            let props = 'location=no,clearcache=yes';
            if (mixin.methods._isIos()) {
                props = props + ',hidenavigationbuttons=yes,toolbarcolor=#ffffff,toolbarposition=top,closebuttoncaption=Fechar';
            }
            cordova.InAppBrowser.open(url, "_blank", props);
        } else {
            window.open(url);
        }
    },

    contactUs({ dispatch }) {
        analytics.logEvent('contact_us');
        dispatch("openExternalUrl", { url: "https://vetalfa.com/contact/" });
    },

    routeBack: async () => {
        if (router.history.current.path.replace('–', '') != window.location.href.split('#')[1].split('?')[0].replace('%E2%80%93', '') || window.history.length < 3) {
            router.replace('/').then(_ => {
                window.location.reload();
            });
        } else {
            router.back();
        }
    },

    routeRedirect({ state }, { name, query, fromOption }) {
        if (fromOption) {
            name = state.from.name;
            query = state.from.query;
            state.from = null;
        }
        router.push({ name, query });
    },

    routePathRedirect({ commit }, { path }) {
        router.push({ path: path.replace("%", "percentage") || "/home" });
    },

    showAlert({ commit }, { text, color, icon }) {
        commit("setAlert", {
            active: true,
            color: color || 'black',
            text: text,
            icon: icon
        });
    },

    showMessage({ commit }, text) {
        commit("setAlert", {
            active: true,
            color: "success",
            text: text
        });
    },

    showError({ commit, dispatch }, args) {
        let error = null;
        let color = 'error';
        let message = args;
        //debugger;
        if (!!args && typeof args !== 'string') {
            if (!!args['event']) {
                dispatch('logEvent', `showError_${args['event']}`);
            }
            if (!!args['back']) {
                dispatch('routeBack');
            }
            message = args['message'];
            error = args['error'];
            console.log(error);
            color = args['color'];
        }
        if (!!error && !!error.message && error.message.includes('Network Error')) {
            message = messages.ERROR_NETWORK;
            color = "black";
        }
        commit("setAlert", {
            active: true,
            color,
            text: message || messages.ERROR
        });
    },

    copyToClipboard({ dispatch }, text) {
        document.removeEventListener("copy", preventCopy, false);
        var dummy = document.getElementById("copy-to-clipboard");
        dummy.classList.remove("copy-to-clipboard");
        dummy.style.display = 'block';
        dummy.value = text;
        dummy.select();
        document.execCommand("copy");
        dummy.style.display = 'none';
        dispatch("showMessage", messages.COPIED_TO_CLIPBOARD);
        document.addEventListener("copy", preventCopy, false);
    },

    async confirmLogout({ commit }) {
        commit("setConfirmModel", {
            active: true,
            text: messages.CONFIRM_LOGOUT,
            action: "general/completeLogout",
            confirmText: labels.CONFIRM_LOGOUT,
            closeText: labels.CANCEL,
            parameters: null
        });
    },

    async completeLogout({ commit, dispatch }) {
        analytics.logEvent('logout');
        await storage
            .storageDeleteWithId("user", null,
                _success => {
                    commit("setUser", null);
                    dispatch("routeRedirect", { name: 'login' });
                }, err => {
                    console.log(err);
                    dispatch("showError", messages.ERROR);
                });
    },

    async saveQuestions({ commit, dispatch, state }, questions) {
        dispatch("logEvent", 'saveQuestions');
        commit("setLoadingOn");
        try {
            await loginServices.saveQuestions(questions, state.user.uid);
            let user = { ...state.user };
            user["questions"] = questions;
            dispatch("saveUserOnApp", { user, hasRedirect: true });
        } catch {
            dispatch("showError", messages.ERROR);
        } finally {
            commit("setLoadingOff");
            commit("setVeterinaryColleges", []);
            dispatch("routeRedirect", { name: "update" });
        }
    },

    async getVeterinaryColleges({ commit, dispatch }) {
        commit("setLoadingOn", true);
        try {
            const payload =
                await entitiesServices.getItem("configuration", 'colleges');
            commit("setVeterinaryColleges", payload.colleges);
        } catch (err) {
            console.log(err);
            dispatch("showError", messages.ERROR);
            commit("setVeterinaryColleges", []);
        } finally {
            commit("setLoadingOff", true);
        }
    },

    saveUserOnApp({ dispatch, commit }, { user, hasRedirect }) {
        storage.storageUpsert("user", null, user,
            function () {
                commit("setUser", user);
                if (hasRedirect) {
                    dispatch("routeRedirect", { name: "update" });
                }
            },
            function (err) {
                console.log(err);
                dispatch("showError", messages.ERROR_SAVE_USER);
                commit("setUser", null);
                if (hasRedirect) {
                    dispatch("routeRedirect", { name: "update" });
                }
            });
    },

    refreshAccessToken: async ({ commit }) => {
        try {
            commit("purchases/setLoading", true, root);
            await loginServices.getToken();
        } catch (e) {
            throw e;
        } finally {
            commit("purchases/setLoading", false, root);
        }

    },

    deleteUserConfirmation: async ({ dispatch, commit, state }) => {
        commit("setLoadingOn", true);
        try {
            await loginServices.deleteUserConfirmation(state.user.uid);
            dispatch("completeLogout");
        } catch (err) {
            dispatch("showError", messages.ERROR);
        } finally {
            commit("setLoadingOff", true);
        }
    },

    partnersRedirect: async ({ dispatch, commit, state }) => {
        try {
            function getParameterByName(name, url = window.location.href) {
                name = name.replace(/[\[\]]/g, '\\$&');
                let regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
                    results = regex.exec(url);
                if (!results) return null;
                if (!results[2]) return '';
                return decodeURIComponent(results[2].replace(/\+/g, ' '));
            }
            let token = getParameterByName('token') || window.sessionStorage.getItem('vetalfa_tkn');
            await commit("setLoadingOn");
            if (!token) {
                throw new Error();
            }
            let payload = await partnersServices.partnersValidateToken(token);

            analytics.setUserId(payload['organization_id']);
            await commit("setUser", {
                displayName: payload['organization_name'],
                uid: payload['organization_id'],
                email: payload['user_email'] || 'partners@vetalfa.com',
                providerId: 'partners',
                questions: {}
            });
            window.sessionStorage.setItem('vetalfa_tkn', token);
            let search_query = getParameterByName('search_query');
            if (!!search_query) {
                setTimeout(() => {
                    dispatch('routePathRedirect', {
                        path: `/items/searches?search_query=${search_query}`
                    });
                }, 100);
            }
            return true;
        } catch (err) {
            window.sessionStorage.removeItem('vetalfa_tkn');
            await commit("setConfirmModel", {
                active: true,
                alert: true,
                text: messages.PARTNERS_SESSION_NOT_ALLOWED,
                callback: () => { window.location.assign(parameters.HOME_URL); },
                confirmText: labels.CONTINUE,
            });
            return false;
        } finally {
            await commit("setLoadingOff");
        }
    },

    saveUserFullName: async ({ commit, state, dispatch }, { displayName }) => {
        try {
            if (!mixin.methods._isValidFullName(displayName) || displayName.length > 50) {
                return dispatch("showAlert", { text: messages.FULL_NAME_ERROR, color: 'black' });
            }
            commit("setLoadingOn");
            state.user['displayName'] = displayName;
            await loginServices.saveUserFullName(state.user.uid, displayName);
            await dispatch('saveUserOnApp', { user: state.user });
        } catch (err) {
            dispatch("showError", messages.ERROR);
        } finally {
            commit("setLoadingOff");
        }
    }

}